/**
 * Pick Your Path — group/subject/bundle picker + details form + add-to-backpack.
 * Vanilla JS, scoped to one section instance via data-section-id, following this theme's
 * existing inline-script convention (see timer.liquid, choose-subject.liquid).
 *
 * Subjects and groups are entirely schema-driven (any count) — this file reads whatever
 * data-subject-target / data-subject-content / data-session-target / data-session-panel
 * values are present in the DOM rather than hardcoding subject names.
 */
(function () {
  'use strict';

  function fetchConfig(type) {
    type = type || 'json';
    return {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/' + type },
    };
  }

  function pad(n) {
    return String(Math.floor(n)).padStart(2, '0');
  }

  function initCountdowns(root) {
    root.querySelectorAll('[data-countdown]').forEach(function (el) {
      var target = new Date(el.getAttribute('data-target'));
      var daysEl = el.querySelector('[data-cd-days]');
      var hoursEl = el.querySelector('[data-cd-hours]');
      var minsEl = el.querySelector('[data-cd-mins]');
      var secsEl = el.querySelector('[data-cd-secs]');

      function tick() {
        var diff = target - Date.now();
        if (isNaN(diff) || diff <= 0) {
          daysEl.textContent = hoursEl.textContent = minsEl.textContent = secsEl.textContent = '00';
          return;
        }
        daysEl.textContent = pad(diff / 86400000);
        hoursEl.textContent = pad((diff % 86400000) / 3600000);
        minsEl.textContent = pad((diff % 3600000) / 60000);
        secsEl.textContent = pad((diff % 60000) / 1000);
      }

      tick();
      setInterval(tick, 1000);
    });
  }

  function initPickYourPath(root) {
    var sessionButtons = root.querySelectorAll('[data-session-target]');
    var sessionPanels = root.querySelectorAll('[data-session-panel]');
    var courseRows = root.querySelectorAll('[data-course-row]');
    var stickybar = root.querySelector('[data-stickybar]');
    var countLabel = root.querySelector('[data-count-label]');
    var noteLabel = root.querySelector('[data-note-label]');
    var totalLabel = root.querySelector('[data-total-label]');
    var addToCartBtn = root.querySelector('[data-add-to-cart]');
    var detailsForm = root.querySelector('[data-details-form]');
    var formFields = root.querySelectorAll('[data-field]');

    function moneyFormat(amount) {
      return Math.round(amount).toLocaleString('en-US') + ' PKR';
    }

    function activeGroupPanel() {
      return root.querySelector('[data-session-panel].is-active') || root;
    }

    // Selections persist across subject tabs (a student may pick from P1, switch to P3, pick
    // there too, and both should count) — only an inactive group/season panel or an inactive
    // individual/bundle mode panel are genuinely mutually-exclusive states that should exclude a row.
    function selectedBoxes() {
      var boxes = [];
      courseRows.forEach(function (row) {
        var sessionPanel = row.closest('[data-session-panel]');
        if (sessionPanel && !sessionPanel.classList.contains('is-active')) return;
        var modePanel = row.closest('[data-mode-panel]');
        if (modePanel && modePanel.hidden) return;
        var cb = row.querySelector('[data-course-checkbox]');
        if (cb && cb.checked) boxes.push(cb);
      });
      return boxes;
    }

    function updateStickybar() {
      var panel = activeGroupPanel();
      var discountPct = parseFloat(panel.getAttribute('data-discount-pct') || '0');
      var checked = selectedBoxes();
      var count = checked.length;

      if (count === 0) {
        stickybar.hidden = true;
        root.classList.remove('has-picked');
        return;
      }

      root.classList.add('has-picked');
      stickybar.hidden = false;

      var total = checked.reduce(function (sum, cb) {
        return sum + parseFloat(cb.getAttribute('data-price') || '0');
      }, 0);

      var subjectContents = panel.querySelectorAll('[data-subject-content]');
      var subjectsInGroup = [];
      subjectContents.forEach(function (el) {
        subjectsInGroup.push(el.getAttribute('data-subject-content'));
      });

      var selectedSubjects = {};
      checked.forEach(function (cb) {
        var row = cb.closest('[data-course-row]');
        if (row) selectedSubjects[row.getAttribute('data-subject')] = true;
      });
      var selectedSubjectCount = Object.keys(selectedSubjects).length;
      var comboEligible = subjectsInGroup.length > 1 && selectedSubjectCount === subjectsInGroup.length;

      var activeSessionBtn = root.querySelector('[data-session-target].is-active');
      var sessionName = activeSessionBtn ? activeSessionBtn.querySelector('.pick-path__session-name').textContent.trim() : '';

      var subjectLabel = '';
      if (selectedSubjectCount === 1) {
        var onlySubjectKey = Object.keys(selectedSubjects)[0];
        var subjectBtn = panel.querySelector('[data-subject-target="' + onlySubjectKey + '"]');
        if (subjectBtn) {
          var titleEl = subjectBtn.querySelector('.pick-path__subject-title');
          subjectLabel = titleEl ? titleEl.textContent.trim() : '';
        }
      }

      countLabel.textContent = count + (count === 1 ? ' item' : ' items') + (subjectLabel ? ' · ' + subjectLabel : '') + (sessionName ? ' · ' + sessionName : '');

      var missing = detailsForm ? countMissingFields() : 0;
      if (missing > 0) {
        noteLabel.textContent = 'almost packed';
      } else if (comboEligible && discountPct > 0) {
        noteLabel.textContent = discountPct + '% off applied at checkout';
      } else {
        noteLabel.textContent = 'details complete — you’re ready';
      }

      totalLabel.textContent = moneyFormat(total);
    }

    // --- session/group toggle ---
    sessionButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var target = btn.getAttribute('data-session-target');
        sessionButtons.forEach(function (b) {
          var active = b === btn;
          b.classList.toggle('is-active', active);
          b.setAttribute('aria-pressed', active ? 'true' : 'false');
        });
        sessionPanels.forEach(function (panel) {
          panel.classList.toggle('is-active', panel.getAttribute('data-session-panel') === target);
        });

        // per-group accent color override, falls back to the section default when unset
        var accentColor = btn.getAttribute('data-accent-color');
        if (accentColor) {
          root.style.setProperty('--pp-green-deep', accentColor);
        } else {
          root.style.removeProperty('--pp-green-deep');
        }

        updateStickybar();
      });
    });

    // --- subject filter (scoped to whichever group panel the button lives in) ---
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-subject-target]');
      if (!btn) return;
      var panel = btn.closest('[data-session-panel]') || root;
      var target = btn.getAttribute('data-subject-target');

      panel.querySelectorAll('[data-subject-target]').forEach(function (b) {
        var active = b === btn;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      panel.querySelectorAll('[data-subject-content]').forEach(function (content) {
        var show = target === 'all' || content.getAttribute('data-subject-content') === target;
        content.classList.toggle('is-active', show);
      });

      // show a subject-name divider between blocks when multiple subjects are visible at once
      var stepMonths = panel.querySelector('.pick-path__step--months');
      if (stepMonths) stepMonths.classList.toggle('is-all-view', target === 'all');

      // only the active subject's "select all" button shows; hidden entirely on the "all subjects" view
      panel.querySelectorAll('[data-select-all-target]').forEach(function (saBtn) {
        var isCurrent = target !== 'all' && saBtn.getAttribute('data-select-all-target') === target;
        saBtn.classList.toggle('is-current-subject', isCurrent);
      });

      // "all subjects" also selects every available course across every subject shown
      if (target === 'all') {
        panel.querySelectorAll('[data-course-checkbox]').forEach(function (cb) {
          if (!cb.disabled) cb.checked = true;
        });
      }

      updateStickybar();
    });

    // --- individual / bundle mode toggle (scoped to whichever subject the button lives in) ---
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-mode-target]');
      if (!btn) return;
      var wrapper = btn.closest('[data-subject-content]');
      if (!wrapper) return;
      var target = btn.getAttribute('data-mode-target');
      var subjectKey = wrapper.getAttribute('data-subject-content');

      wrapper.querySelectorAll('[data-mode-target]').forEach(function (b) {
        var active = b === btn;
        b.classList.toggle('is-active', active);
        b.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
      wrapper.querySelectorAll('[data-mode-panel]').forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-mode-panel') !== target;
      });

      // "select all" only makes sense in individual mode — bundle rows are already locked
      var saBtn = root.querySelector('[data-select-all-target="' + subjectKey + '"]');
      if (saBtn) saBtn.classList.toggle('pick-path__select-all--hidden-mode', target === 'bundle');

      updateStickybar();
    });

    // --- "select all" (targets its subject's course list by data-select-all-target, since the button now lives in the step heading) ---
    root.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-select-all]');
      if (!btn) return;
      var subjectKey = btn.getAttribute('data-select-all-target');
      var wrapper = root.querySelector('[data-subject-content="' + subjectKey + '"]');
      if (!wrapper) return;
      var scope = wrapper.querySelector('[data-mode-panel="individual"]') || wrapper;
      var boxes = Array.prototype.slice
        .call(scope.querySelectorAll('[data-course-checkbox]'))
        .filter(function (cb) { return !cb.disabled; });
      var allChecked = boxes.length > 0 && boxes.every(function (cb) { return cb.checked; });
      boxes.forEach(function (cb) { cb.checked = !allChecked; });
      btn.classList.toggle('is-active', !allChecked);
      updateStickybar();
    });

    // --- checkboxes ---
    courseRows.forEach(function (row) {
      var cb = row.querySelector('[data-course-checkbox]');
      if (cb) cb.addEventListener('change', updateStickybar);
    });

    // --- syllabus expand/collapse ---
    root.querySelectorAll('[data-syllabus-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var row = btn.closest('[data-course-row]');
        var panel = row.querySelector('[data-syllabus-panel]');
        var label = btn.querySelector('[data-syllabus-toggle-label]');
        var open = panel.hidden;
        panel.hidden = !open;
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        btn.classList.toggle('is-open', open);
        if (label) label.textContent = open ? 'hide' : 'syllabus';
      });
    });

    // --- details form: blur-only validation ---
    function countMissingFields() {
      var missing = 0;
      formFields.forEach(function (field) {
        if (!field.checkValidity()) missing += 1;
      });
      return missing;
    }

    formFields.forEach(function (field) {
      field.addEventListener('blur', function () {
        field.classList.add('is-touched');
        field.classList.toggle('is-invalid', !field.checkValidity());
      });
      field.addEventListener('input', function () {
        if (field.classList.contains('is-touched')) {
          field.classList.toggle('is-invalid', !field.checkValidity());
        }
        updateStickybar();
      });
      field.addEventListener('change', function () {
        field.classList.add('is-touched');
        field.classList.toggle('is-invalid', !field.checkValidity());
        updateStickybar();
      });
    });

    // --- add to backpack ---
    if (addToCartBtn) {
      addToCartBtn.addEventListener('click', function () {
        var checked = selectedBoxes();
        if (checked.length === 0) return;

        var invalidField = null;
        formFields.forEach(function (field) {
          if (!invalidField && !field.checkValidity()) invalidField = field;
        });

        if (invalidField) {
          formFields.forEach(function (field) {
            field.classList.add('is-touched');
            field.classList.toggle('is-invalid', !field.checkValidity());
          });
          if (detailsForm) {
            detailsForm.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          setTimeout(function () {
            invalidField.focus();
            invalidField.reportValidity();
          }, 350);
          updateStickybar();
          return;
        }

        var properties = {};
        formFields.forEach(function (field) {
          var key = field.getAttribute('data-field-label') || field.name;
          properties[key] = field.value.trim();
        });

        var items = checked.map(function (cb) {
          return {
            id: parseInt(cb.getAttribute('data-variant-id'), 10),
            quantity: 1,
            properties: properties,
          };
        });

        addToCartBtn.disabled = true;
        addToCartBtn.classList.add('is-loading');

        var sectionsToRender = [];
        var cartIconBubble = document.querySelector('#cart-icon-bubble');
        var cartDrawer = document.querySelector('cart-drawer');
        if (cartIconBubble) sectionsToRender.push('cart-icon-bubble');
        if (cartDrawer) sectionsToRender.push('cart-drawer');

        var body = JSON.stringify({
          items: items,
          sections: sectionsToRender,
          sections_url: window.location.pathname,
        });

        fetch(window.routes ? window.routes.cart_add_url : '/cart/add.js', Object.assign({}, fetchConfig(), { body: body }))
          .then(function (response) { return response.json(); })
          .then(function (response) {
            addToCartBtn.disabled = false;
            addToCartBtn.classList.remove('is-loading');

            if (response.status) {
              console.error('pick-your-path: add to cart failed', response);
              return;
            }

            document.dispatchEvent(new CustomEvent('cart:refresh'));

            if (response.sections && cartIconBubble && response.sections['cart-icon-bubble']) {
              cartIconBubble.innerHTML = new DOMParser()
                .parseFromString(response.sections['cart-icon-bubble'], 'text/html')
                .querySelector('#shopify-section-cart-icon-bubble')
                ? new DOMParser().parseFromString(response.sections['cart-icon-bubble'], 'text/html').querySelector('#shopify-section-cart-icon-bubble').innerHTML
                : response.sections['cart-icon-bubble'];
            }

            if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
              cartDrawer.renderContents(response);
            } else if (cartDrawer && typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            }
          })
          .catch(function (error) {
            addToCartBtn.disabled = false;
            addToCartBtn.classList.remove('is-loading');
            console.error('pick-your-path: add to cart error', error);
          });
      });
    }

    updateStickybar();
    initCountdowns(root);
  }

  function boot() {
    var sectionId = document.currentScript && document.currentScript.getAttribute('data-section-id');
    var root = sectionId ? document.getElementById('pick-path-' + sectionId) : document.querySelector('[id^="pick-path-"]');
    if (root) initPickYourPath(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[id^="pick-path-"]');
    if (root) initPickYourPath(root);
  });
})();
